import { ENTITY_RECORD_PATH } from './entity.record.contracts';

export interface EntityRecordCopy {
  kind: 'link' | 'number';
  text: string;
}

/**
 * What a record's copy action puts on the clipboard: the record's absolute URL, or with Shift its
 * document number. Only generic `/entity/...` pages are offered — a legacy canonical URL is not a v2
 * link to share — and Shift falls back to the link while the record has no number yet (a draft).
 */
export function resolveEntityRecordCopy(canonicalUrl: string | null | undefined, origin: string, documentNumber: string | null, shift: boolean): EntityRecordCopy | null {

  if (!canonicalUrl?.startsWith(`/${ENTITY_RECORD_PATH}/`)) return null;
  if (shift && documentNumber) return { kind: 'number', text: documentNumber };
  return { kind: 'link', text: `${origin}${canonicalUrl}` };
}

/**
 * Puts `text` on the clipboard without leaving `root`.
 *
 * The Edit dialog's focus trap restores focus when a textarea is appended to `document.body`,
 * which is what Angular CDK's Clipboard does, so that write copies nothing while Preview (no trap)
 * succeeds. `navigator.clipboard.writeText` does not move focus. The textarea fallback is appended
 * inside `root`, so the trap treats it as its own.
 * Who calls it: the financial-document copy button. Guarantees: resolves true only after a write.
 */
export async function copyTextInsideFocusRoot(document: Document, root: HTMLElement, text: string): Promise<boolean> {

  const clipboard = document.defaultView?.navigator.clipboard;
  if (clipboard?.writeText) {
    try {
      await clipboard.writeText(text);
      return true;
    } catch {
      // Permission or a missing user gesture: the in-root textarea still works.
    }
  }
  return copyWithTextarea(document, root, text);
}

function copyWithTextarea(document: Document, root: HTMLElement, text: string): boolean {

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('aria-hidden', 'true');
  textarea.readOnly = true;
  const styles = textarea.style;
  styles.position = 'fixed';
  styles.top = '0';
  styles.opacity = '0';
  styles.left = '-999em';
  root.appendChild(textarea);
  const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  let successful = false;
  try {
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    successful = document.execCommand('copy');
  } catch {
    successful = false;
  } finally {
    textarea.remove();
    if (previous && root.contains(previous)) previous.focus();
  }
  return successful;
}
